import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/layout/Breadcrumbs";
import SectionHeading from "../components/blocks/SectionHeading";
import CTASection from "../components/blocks/CTASection";
import { KNOWLEDGE_CATEGORIES } from "../data/content";
import { INSIGHTS } from "../data/insights";

export default function KnowledgePage() {
  return (
    <>
      <SEO
        title="Knowledge Hub — Insights, Frameworks, Research & Toolkits | The Vantage Institute™"
        description="Executive insights, frameworks, applied research, case studies, videos, podcasts, webinars and practical toolkits from The Vantage Institute."
        path="/knowledge"
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Knowledge Hub" }]} />

      <section className="bg-navy">
        <div className="container-x py-16 sm:py-20">
          <p className="eyebrow-light">Knowledge Hub</p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-semibold leading-tight text-white sm:text-5xl">
            Intellectual capital, in the open.
          </h1>
          <span className="gold-rule mt-8 !w-24" aria-hidden="true" />
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-platinum md:text-lg">
            The Knowledge Hub is where the Institute publishes its thinking — insights, frameworks, research and
            practical tools. Built to be read in minutes and used for years.
          </p>
        </div>
      </section>

      <section className="container-x py-16 sm:py-24" data-testid="knowledge-categories">
        <SectionHeading
          eyebrow="Explore by Category"
          title="Eight ways into Vantage thinking."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {KNOWLEDGE_CATEGORIES.map((k) => (
            <div key={k.title} className="card card-hover flex h-full flex-col" data-testid={`knowledge-cat-${k.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}>
              <h3 className="font-sub text-base font-semibold text-navy">{k.title}</h3>
              <span className="gold-rule mt-3 !w-10" aria-hidden="true" />
              <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">{k.text}</p>
              <p className="mt-4 border-t border-platinum pt-3 font-sub text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-gold-deep">
                {k.status}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-paper" data-testid="knowledge-featured">
        <div className="container-x py-16 sm:py-24">
          <SectionHeading
            eyebrow="Executive Insights"
            title="The first insight series."
            intro="Original thinking from the Institute — written to be read in minutes and argued with for longer."
          />
          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {INSIGHTS.map((a) => (
              <article key={a.slug} className="card card-hover flex h-full flex-col" data-testid={`insight-card-${a.slug}`}>
                <p className="font-sub text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-gold-deep">
                  Executive Insight • {a.minutes} min read
                </p>
                <h3 className="mt-3 font-sub text-base font-semibold leading-snug text-navy">{a.title}</h3>
                <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate">{a.excerpt}</p>
                <Link
                  to={`/knowledge/insights/${a.slug}`}
                  className="text-link mt-5 inline-flex min-h-[44px] items-center"
                  data-testid={`insight-card-${a.slug}-link`}
                >
                  Read the Insight
                </Link>
              </article>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap gap-4">
            <Link to="/thinking-toolbox" className="btn-navy" data-testid="knowledge-cta-toolbox">
              Explore the Thinking Toolbox™
            </Link>
            <Link to="/programmes" className="btn-outline-navy" data-testid="knowledge-cta-programmes">
              Explore Programmes
            </Link>
          </div>
        </div>
      </section>

      <CTASection
        title="Get new thinking as it is published."
        text="The Vantage Briefing — the Institute's periodic executive briefing — launches shortly. Register your interest via the contact page."
        primaryLabel="Talk to Vantage"
        primaryTo="/contact"
        secondaryLabel="Explore Programmes"
        secondaryTo="/programmes"
        testId="knowledge-cta"
      />
    </>
  );
}
